import React, { useEffect, useState, useMemo, useCallback, memo } from 'react';
import { 
  getTournamentUserRankings,
  getTournamentTeamRankings,
  getUserMaintenanceDetails,
  UserRanking, 
  TeamRanking 
} from '../../services/rankingService';
import './MaintenanceRankings.css';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { 
  FaMedal, 
  FaRunning, 
  FaSearch, 
  FaCalendarAlt, 
  FaUser, 
  FaUsers, 
  FaTrophy, 
  FaChartLine, 
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaTable,
  FaTh
} from 'react-icons/fa';
import DatePickerCustom from '../DatePickerCustom';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { useTheme } from '../../context/ThemeContext';

interface MaintenanceRankingsProps {
  tournamentId: number;
  userId?: number;
  displayType: 'users' | 'teams';
  viewType?: 'minimal' | 'full';
  startDate?: string;
  endDate?: string;
}

// Enum cho các loại thời gian hiển thị
enum TimeRangeType {
  WEEK = 'week',
  MONTH = 'month',
  TOURNAMENT = 'tournament'
}

// Enum cho kiểu hiển thị đội
enum TeamViewType {
  TABLE = 'table',
  CARDS = 'cards'
}

// Component con cho hiển thị row người dùng với memo
const UserRankingRow = memo(({ ranking, getMedalColor, renderProgressBar }: {
  ranking: UserRanking;
  getMedalColor: (rank: number) => string;
  renderProgressBar: (ratio: number) => React.ReactNode;
}) => (
  <tr key={ranking.userId}>
    <td className="rank-cell">
      {ranking.rank <= 3 ? (
        <FaMedal style={{ color: getMedalColor(ranking.rank), fontSize: '1.4rem' }} />
      ) : (
        ranking.rank
      )}
    </td>
    <td className="user-cell">
      <div className="avatar-medal-wrapper">
        <img src={ranking.profileImageUrl || '/default-avatar.png'} alt={ranking.fullName} className="user-avatar" />
        {ranking.rank <= 3 && (
          <FaMedal className={`medal-icon medal-icon-inline rank-${ranking.rank}`} style={{ color: getMedalColor(ranking.rank) }} />
        )}
      </div>
      <div className="user-info">
        <div className="user-name">{ranking.fullName}</div>
        <div className="user-username">@{ranking.username}</div>
      </div>
    </td>
    <td className="ratio-cell">
      {renderProgressBar(ranking.maintenanceRatio)}
      <div className="days-info">
        <span className="valid-days">{ranking.validDays}</span>
        <span className="days-separator">/</span>
        <span className="total-days">{ranking.totalDays}</span>
        <span className="days-label">ngày</span>
      </div>
    </td>
    <td className="distance-cell">
      <div className="distance-value">{ranking.totalDistance.toFixed(1)}</div>
      <div className="distance-unit">km</div>
    </td>
  </tr>
));

// Component con cho hiển thị row đội với memo
const TeamRankingRow = memo(({ ranking, getMedalColor, renderProgressBar }: {
  ranking: TeamRanking;
  getMedalColor: (rank: number) => string;
  renderProgressBar: (ratio: number) => React.ReactNode;
}) => (
  <tr key={ranking.userId}>
    <td className="rank-cell">
      {ranking.rank <= 3 ? (
        <FaMedal style={{ color: getMedalColor(ranking.rank), fontSize: '1.4rem' }} />
      ) : (
        ranking.rank
      )}
    </td>
    <td className="user-cell">
      <div className="team-info-container">
        <div className="team-icon-wrapper">
          <div className="team-icon">
            <FaUsers className="team-icon-inner" />
            {ranking.rank <= 3 && (
              <FaMedal className={`medal-icon medal-icon-inline rank-${ranking.rank}`} style={{ color: getMedalColor(ranking.rank) }} />
            )}
          </div>
        </div>
        <div className="team-info">
          <div className="team-name">{ranking.fullName}</div>
          <div className="team-code">@{ranking.username}</div>
        </div>
      </div>
    </td>
    <td className="ratio-cell">
      {renderProgressBar(ranking.maintenanceRatio)}
      <div className="days-info">
        <span className="valid-days">{ranking.validDays}</span>
        <span className="days-separator">/</span>
        <span className="total-days">{ranking.totalDays}</span>
        <span className="days-label">ngày</span>
      </div>
    </td>
    <td className="distance-cell">
      <div className="distance-value">{ranking.totalDistance.toFixed(1)}</div>
      <div className="distance-unit">km</div>
    </td>
  </tr>
));

// Component con cho hiển thị thẻ đội với memo
const TeamCard = memo(({ ranking, getMedalColor, renderProgressBar }: {
  ranking: TeamRanking;
  getMedalColor: (rank: number) => string;
  renderProgressBar: (ratio: number) => React.ReactNode;
}) => (
  <div className="team-card" key={ranking.userId}>
    <div className="team-card-header">
      {ranking.rank <= 3 ? (
        <div className="team-card-rank top-rank">
          <FaMedal style={{ color: getMedalColor(ranking.rank), fontSize: '1.6rem' }} />
        </div>
      ) : (
        <div className="team-card-rank">{ranking.rank}</div>
      )}
      <div className="team-icon">
        <FaUsers className="team-icon-inner" />
      </div>
      <div className="team-card-info">
        <h3 className="team-card-name">{ranking.fullName}</h3>
        <div className="team-card-code">@{ranking.username}</div>
      </div>
    </div>
    
    <div className="team-card-body">
      <div style={{ margin: '0 auto', width: '100px', height: '100px' }}>
        {renderProgressBar(ranking.maintenanceRatio)}
      </div>
      
      <div className="team-card-stats">
        <div className="team-card-stat">
          <span className="team-card-stat-value">{ranking.validDays}/{ranking.totalDays}</span>
          <span className="team-card-stat-label">Ngày duy trì</span>
        </div>
        <div className="team-card-stat">
          <span className="team-card-stat-value">{ranking.totalDistance.toFixed(1)}</span>
          <span className="team-card-stat-label">Km</span>
        </div>
      </div>
    </div>
  </div>
));

const MaintenanceRankings: React.FC<MaintenanceRankingsProps> = ({
  tournamentId,
  userId,
  displayType = 'users',
  viewType = 'full',
  startDate: initialStartDate,
  endDate: initialEndDate
}) => {
  const { theme } = useTheme();
  const [userRankings, setUserRankings] = useState<UserRanking[]>([]);
  const [teamRankings, setTeamRankings] = useState<TeamRanking[]>([]);
  const [userDetails, setUserDetails] = useState<UserRanking | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string | undefined>(initialStartDate);
  const [endDate, setEndDate] = useState<string | undefined>(initialEndDate);
  const [timeRangeType, setTimeRangeType] = useState<TimeRangeType>(TimeRangeType.TOURNAMENT);
  const [teamViewType, setTeamViewType] = useState<TeamViewType>(TeamViewType.TABLE);

  // Cập nhật startDate và endDate khi props thay đổi
  useEffect(() => {
    console.log("MaintenanceRankings - Nhận giá trị mới từ props:", { initialStartDate, initialEndDate });
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    const fetchRankings = async () => {
      setLoading(true);
      setError(null);
      console.log("MaintenanceRankings - Đang fetch data với:", { tournamentId, startDate, endDate });
      try {
        if (displayType === 'users') {
          const rankings = await getTournamentUserRankings(tournamentId, startDate, endDate);
          setUserRankings(rankings);
        } else {
          const rankings = await getTournamentTeamRankings(tournamentId, startDate, endDate);
          setTeamRankings(rankings);
        }

        if (userId) {
          const details = await getUserMaintenanceDetails(tournamentId, userId, startDate, endDate);
          setUserDetails(details);
        }
      } catch (err: any) {
        setError(err.message || 'Không thể tải bảng xếp hạng');
      } finally {
        setLoading(false);
      }
    };

    fetchRankings();
  }, [tournamentId, displayType, userId, startDate, endDate]);

  // Sử dụng useCallback cho các hàm xử lý
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleStartDateChange = useCallback((date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : undefined;
    setStartDate(dateStr);
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    const dateStr = date ? date.toISOString().split('T')[0] : undefined;
    setEndDate(dateStr);
  }, []);

  const handleTimeRangeChange = useCallback((timeRange: TimeRangeType) => {
    setTimeRangeType(timeRange);
    
    const today = new Date();
    
    switch (timeRange) {
      case TimeRangeType.WEEK:
        // Lấy ngày đầu tuần và cuối tuần hiện tại
        setStartDate(format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        setEndDate(format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd'));
        break;
      case TimeRangeType.MONTH:
        // Lấy ngày đầu tháng và cuối tháng hiện tại
        setStartDate(format(startOfMonth(today), 'yyyy-MM-dd'));
        setEndDate(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case TimeRangeType.TOURNAMENT:
        // Không lọc theo ngày
        setStartDate(undefined);
        setEndDate(undefined);
        break;
    }
  }, []);

  const toggleTeamViewType = useCallback(() => {
    setTeamViewType(prev => prev === TeamViewType.TABLE ? TeamViewType.CARDS : TeamViewType.TABLE);
  }, []);

  // Sử dụng useCallback cho các hàm render và helper
  const getMedalColor = useCallback((rank: number): string => {
    switch (rank) {
      case 1: return 'gold';
      case 2: return 'silver';
      case 3: return '#cd7f32'; // bronze
      default: return '#aaaaaa';
    }
  }, []);

  const renderProgressBar = useCallback((ratio: number) => {
    const roundedRatio = Math.round(ratio * 10) / 10;
    
    let color = '#e74c3c'; // red for low ratio
    if (roundedRatio >= 80) color = '#27ae60'; // green for high ratio
    else if (roundedRatio >= 50) color = '#f39c12'; // orange for medium ratio
    else if (roundedRatio >= 30) color = '#e67e22'; // darker orange for low-medium

    return (
      <div className="ratio-progress">
        <CircularProgressbar
          value={roundedRatio}
          text={`${roundedRatio}%`}
          styles={buildStyles({
            textSize: '28px',
            pathColor: color,
            textColor: '#333',
            trailColor: '#eee',
          })}
        />
      </div>
    );
  }, []);

  // Sử dụng useMemo để lọc dữ liệu
  const filteredUserRankings = useMemo(() => searchTerm.trim() !== '' 
    ? userRankings.filter(user => 
        user.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
        user.username.toLowerCase().includes(searchTerm.toLowerCase()))
    : userRankings,
  [userRankings, searchTerm]);

  const filteredTeamRankings = useMemo(() => searchTerm.trim() !== '' 
    ? teamRankings.filter(team => 
        team.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
        team.username.toLowerCase().includes(searchTerm.toLowerCase()))
    : teamRankings,
  [teamRankings, searchTerm]);

  // Thêm animation ClassNames dựa trên trạng thái hiển thị
  const [isTableView, setIsTableView] = useState(true);
  
  // Theo dõi thay đổi teamViewType và áp dụng hiệu ứng
  useEffect(() => {
    if (displayType === 'teams') {
      const newIsTableView = teamViewType === TeamViewType.TABLE;
      if (newIsTableView !== isTableView) {
        setIsTableView(newIsTableView);
      }
    }
  }, [teamViewType, displayType, isTableView]);

  const renderUserDetailsCard = () => {
    if (!userDetails) return null;
    
    return (
      <div className="user-details-card">
        <div className="user-details-header">
          <img src={userDetails.profileImageUrl || '/default-avatar.png'} alt={userDetails.fullName} className="user-details-avatar" />
          <div className="user-details-info">
            <h3 className="user-details-name">{userDetails.fullName}</h3>
            <div className="user-details-username">@{userDetails.username}</div>
          </div>
          {userDetails.rank <= 3 ? (
            <div className="user-details-rank">
              <FaMedal className="medal-icon-large" style={{ color: getMedalColor(userDetails.rank) }} />
              <span className="rank-number">#{userDetails.rank}</span>
            </div>
          ) : userDetails.rank <= 10 ? (
            <div className="user-details-rank">
              <FaTrophy className="medal-icon-large" style={{ color: '#6c757d' }} />
              <span className="rank-number">#{userDetails.rank}</span>
            </div>
          ) : (
            <div className="user-details-rank">
              <span className="rank-number">#{userDetails.rank}</span>
            </div>
          )}
        </div>
        <div className="user-details-stats">
          <div className="user-details-ratio">
            <div className="stat-label">
              <FaChartLine style={{ marginRight: '8px' }} /> Tỷ lệ duy trì
            </div>
            <div className="stat-progress">
              {renderProgressBar(userDetails.maintenanceRatio)}
            </div>
            <div className="days-detail">
              <div className="days-row">
                <div className="days-label">
                  <FaCheck style={{ marginRight: '5px', color: '#28a745' }} /> 
                  Ngày hoạt động:
                </div>
                <div className="days-value">{userDetails.validDays} ngày</div>
              </div>
              <div className="days-row">
                <div className="days-label">
                  <FaCalendarAlt style={{ marginRight: '5px', color: '#6c757d' }} /> 
                  Tổng số ngày:
                </div>
                <div className="days-value">{userDetails.totalDays} ngày</div>
              </div>
            </div>
          </div>
          <div className="user-details-distance">
            <div className="stat-label">
              <FaRunning style={{ marginRight: '8px' }} /> Tổng khoảng cách
            </div>
            <div className="distance-stat">
              <FaRunning className="distance-icon" />
              <div>
              <span className="distance-value-large">{userDetails.totalDistance.toFixed(1)}</span>
              <span className="distance-unit-large">km</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) return (
    <div className="rankings-loading-container" style={{ minHeight: "200px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <FaSpinner className="fa-spin" style={{ marginRight: '10px', fontSize: '24px', color: '#20757A' }} /> 
      <span>Đang tải bảng xếp hạng...</span>
    </div>
  );
  
  if (error) return (
    <div className="rankings-error">
      <div><FaExclamationTriangle style={{ marginRight: '10px' }} /> Lỗi</div>
      <div>{error}</div>
    </div>
  );

  if (viewType === 'minimal' && userDetails) {
    return renderUserDetailsCard();
  }

  return (
    <div className={`rankings-container ${theme}`}>
      <div className="rankings-filters">
        <div className="time-range-filters">
          <button 
            className={`filter-btn ${timeRangeType === TimeRangeType.WEEK ? 'active' : ''}`} 
            onClick={() => handleTimeRangeChange(TimeRangeType.WEEK)}
          >
            <FaCalendarAlt style={{ marginRight: '8px' }} /> Tuần này
          </button>
          <button 
            className={`filter-btn ${timeRangeType === TimeRangeType.MONTH ? 'active' : ''}`} 
            onClick={() => handleTimeRangeChange(TimeRangeType.MONTH)}
          >
            <FaCalendarAlt style={{ marginRight: '8px' }} /> Tháng này
          </button>
          <button 
            className={`filter-btn ${timeRangeType === TimeRangeType.TOURNAMENT ? 'active' : ''}`} 
            onClick={() => handleTimeRangeChange(TimeRangeType.TOURNAMENT)}
          >
            <FaTrophy style={{ marginRight: '8px' }} /> Toàn giải
          </button>
        </div>
        <div className="custom-date-filters">
          <div className="date-range-picker">
            <div className="date-picker-label">
              <FaCalendarAlt /> Tùy chỉnh thời gian:
            </div>
            <div className="date-inputs">
              <DatePickerCustom 
                label="Từ" 
                value={startDate || ''} 
                onChange={handleStartDateChange} 
                className="date-input"
              />
              <DatePickerCustom 
                label="Đến" 
                value={endDate || ''} 
                onChange={handleEndDateChange} 
                className="date-input"
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`rankings-table-container ${displayType === 'teams' && teamViewType === TeamViewType.CARDS ? 'card-view' : ''}`}>
        <div className="rankings-header">
        <h3 className="rankings-title">
            {displayType === 'users' ? (
              <>
                <FaUser style={{ marginRight: '10px' }} /> Xếp hạng cá nhân
              </>
            ) : (
              <>
                <FaUsers style={{ marginRight: '10px' }} /> Xếp hạng đội
              </>
            )}
            {timeRangeType !== TimeRangeType.TOURNAMENT && (
              <span className="rankings-subtitle">
                {timeRangeType === TimeRangeType.WEEK ? ' - Tuần này' : ' - Tháng này'}
              </span>
            )}
            {startDate && endDate && timeRangeType === TimeRangeType.TOURNAMENT && (
              <span className="rankings-subtitle">
                ({startDate} - {endDate})
              </span>
            )}
        </h3>
          
          {displayType === 'teams' && (
            <div className="view-toggle">
              <button 
                className={`view-toggle-btn ${teamViewType === TeamViewType.TABLE ? 'active' : ''}`}
                onClick={() => setTeamViewType(TeamViewType.TABLE)}
                title="Hiển thị dạng bảng"
              >
                <FaTable />
              </button>
              <button 
                className={`view-toggle-btn ${teamViewType === TeamViewType.CARDS ? 'active' : ''}`}
                onClick={() => setTeamViewType(TeamViewType.CARDS)}
                title="Hiển thị dạng thẻ"
              >
                <FaTh />
              </button>
            </div>
          )}
        </div>

        <div className="search-box">
          <div className="search-form">
            <input
              type="text"
              className="search-input"
              placeholder={`Tìm kiếm ${displayType === 'users' ? 'người dùng' : 'đội'}...`}
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div className="search-actions">
              <FaSearch className="search-icon" />
            </div>
          </div>
        </div>
        
        {displayType === 'users' || (displayType === 'teams' && teamViewType === TeamViewType.TABLE) ? (
        <table className="rankings-table">
          <thead>
            <tr>
              <th className="rank-header">Hạng</th>
              <th className="user-header">{displayType === 'users' ? 'Người dùng' : 'Đội'}</th>
              <th className="ratio-header">Tỷ lệ duy trì</th>
              <th className="distance-header">Khoảng cách</th>
            </tr>
          </thead>
          <tbody>
            {displayType === 'users' 
                ? filteredUserRankings.map((ranking, index) => (
                    <UserRankingRow 
                      key={ranking.userId} 
                      ranking={ranking} 
                      getMedalColor={getMedalColor}
                      renderProgressBar={renderProgressBar}
                    />
                  ))
                : filteredTeamRankings.map((ranking, index) => (
                    <TeamRankingRow
                      key={ranking.userId}
                      ranking={ranking}
                      getMedalColor={getMedalColor}
                      renderProgressBar={renderProgressBar}
                    />
                  ))
            }
          </tbody>
        </table>
        ) : (
          <div className="team-ranking-cards">
            {filteredTeamRankings.map((ranking, index) => (
              <TeamCard
                key={ranking.userId}
                ranking={ranking}
                getMedalColor={getMedalColor}
                renderProgressBar={renderProgressBar}
              />
            ))}
          </div>
        )}
        
        {((displayType === 'users' && filteredUserRankings.length === 0) || 
           (displayType === 'teams' && filteredTeamRankings.length === 0)) && (
          searchTerm.trim() !== '' ? (
            <div className="rankings-empty">
              <div className="empty-icon"><FaSearch /></div>
              <div className="empty-message">Không tìm thấy kết quả phù hợp với "{searchTerm}"</div>
            </div>
          ) : (
            <div className="rankings-empty">
              <div className="empty-icon">🏆</div>
              <div className="empty-message">Chưa có dữ liệu xếp hạng</div>
            </div>
          )
        )}
      </div>
      
      {userId && userDetails && renderUserDetailsCard()}
    </div>
  );
};

export default MaintenanceRankings; 